import {Component} from '@angular/core';
import {QueryService} from '@pt/api-client';
import {AbstractWidgetDirective} from '../../abstract-widget.directive';
import {ActionsSubject, Store} from '@ngrx/store';
import { DialogType, REQUIRED_VALIDATOR_KEY, UiWidget, WidgetConfiguration, WidgetValidator } from '@pt/pt-models';
import {TranslateService} from '@ngx-translate/core';
import {
  WaaArgs,
  WaaQueryMapping,
  WaaHeader,
  WaaImplementedValue, WaaValue, WaaPreview
} from './models/widget-app-assessment-v0-args.dto';
import {WidgetUtils} from '../../widget.utils';
import {WidgetDialogService} from '../../../services/widget-dialog.service';
import {NgxPermissionsService} from 'ngx-permissions';
import {WaaUtils} from './waa.utils';
import { DialogService } from '../../../../services/dialog.service';

/**
 * Supporta l'autosave
 */
@Component({
  selector: 'pt-widget-app-assessment-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">

      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class="w-100"
        [label]="widget?.label"
        [help]="widget?.help"

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]="internalStatus.showCancelButton"
        [showCommentButton]="internalStatus.showCommentButton"
        [showEditButton]="internalStatus.showEditButton"
        [showReloadButton]="internalStatus.showReloadButton"
        [showSaveButton]="internalStatus.showSaveButton"
        [showOptButton]="internalStatus.showOptButton"
        [disabledResetAllButton]="internalStatus.disabledResetAllButton"
        [enableResetAll]="true"
        (clickResetAll)="clickResetAll()"

        [disabledCancelButton]="internalStatus.disabledCancelButton"
        [disabledCommentButton]="internalStatus.disabledCommentButton"
        [disabledEditButton]="internalStatus.disabledEditButton"
        [disabledOptButton]="internalStatus.disabledOptButton"
        [disabledReloadButton]="internalStatus.disabledReloadButton"
        [disabledSaveButton]="internalStatus.disabledSaveButton"
        [loading]="internalStatus.isLoading"

        (clickSaveWidget)="saveWidget()"
        (clickResetWidget)="resetWidget(widget)"
        (clickEditWidget)="dispatchEditState()"
        (clickReloadWidget)="reloadWidget(widget)"
        (clickCreateOpt)="createOptWidget()"
        (clickCommentWidget)="clickCommentWidget()">
      </pt-widget-header>

      <ng-container *ngIf="!internalStatus.showOptButton">
        <pt-waa-editor
          [noResponseLabel]="'LABELS.NO_RESPONSE' | translate"
          [show]="internalStatus.viewMode === 'EDIT' || internalStatus.viewMode === 'LOADING' || internalStatus.viewMode === 'PREVIEW'"
          [disabled]="internalStatus.viewMode === 'LOADING' || internalStatus.viewMode === 'PREVIEW'"
          [data]="previewData"
          [widgetValue]="currentValue"
          (changeWidgetValue)="changeWidgetValue($event)">
        </pt-waa-editor>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]="internalStatus.errorMessage | async">
        </pt-widget-error>

        <pt-widget-footer>
        </pt-widget-footer>

      </ng-container>

    </div>

  `
})
export class WidgetAppAssessmentV0Component extends AbstractWidgetDirective<WaaPreview> {
  public flat: boolean;
  public group: boolean;

  queryMappingValue: WaaQueryMapping[];
  header: WaaHeader;
  implementedValues: WaaImplementedValue[];
  // la funzione is valid viene utilizzata anche dalla classe astratta e dall'HTML.
  // il suo valore è il risultato di un form che creo ogni volta che mi cambia il valore settando di conseguenza questa variabile
  // formValid = false;
  currentValue: WaaValue[];

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              private dialogService: DialogService,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    const args = widget.args as WaaArgs;
    this.service.resolve(args.query).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = args.query.mapping;
        this.currentValue = widget.value ? widget.value : []; // FAKE_WidgetAppAssessmentV0ValueDto;
        this.implementedValues = args.data?.implementedValues;
        this.header = args.header;
        this.queryMappingValue = WidgetUtils.mappingQueryResponse(queryResponse, mapping) as WaaQueryMapping[];
        const preview: WaaPreview = {
          header: this.header,
          queryMappingValue: this.queryMappingValue,
          implementedValues: this.implementedValues
        };
        this.valueController(widget, this.queryMappingValue);
        this.checkValidity();
        this.dispatchCompleteState(preview);
      }, () => {
        this.dispatchErrorState();
      }
    );
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = [];
    widget.comment = this.comment;
    this.currentValue.forEach((c: WaaValue) => {
      widget.value.push({
        uuid: c.uuid,
        note: c.note ? c.note : undefined,
        implementedValue: c.implementedValue !== null && c.implementedValue !== undefined && c.implementedValue !== -1000 ? c.implementedValue : undefined
      })
    })
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  changeWidgetValue($event: WaaValue) {
    const newValue: WaaValue[] = [];
    let replacedValue = false;
    for (const v of this.currentValue) {
      if (v.uuid === $event.uuid) {
        newValue.push($event)
        replacedValue = true;
      } else {
        newValue.push(v);
      }
    }

    if (!replacedValue) {
      newValue.push($event);
    }
    this.currentValue = newValue;
    this.checkValidity();

    if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
      this.saveWidget();
    }
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: true, supportValidation: false};
  }

  private checkValidity() {
    if (this.widget?.opt) {
      this.isValid$.next(true);
    } else {
      if (this.widget.validators?.find((v: WidgetValidator) => v.key === REQUIRED_VALIDATOR_KEY)) {

        const currentValidValue = this.currentValue?.filter((c) => c.implementedValue !== undefined && c.implementedValue !== -1000) || [];
        const isValid = currentValidValue?.length > 0 && this.queryMappingValue?.length > 0 && currentValidValue?.length === this.queryMappingValue?.length;
        this.isValid$.next(isValid)
      } else {
        this.isValid$.next(true);
      }
    }
  }

  private valueController(widget: UiWidget, data: WaaQueryMapping[]) {
    const noData = !data || data?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }

    const widgetValue: WaaValue[] = widget?.value ? widget?.value : [];
    const validValue: WaaValue[] = WaaUtils.getValidValue(widgetValue, data);
    if (widgetValue.length !== validValue?.length) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }
  }


  clickResetAll() {
    const title = this.translateService.stream('TITLE.WIDGET_RESET_ALL');
    const message = this.translateService.stream('MESSAGE.WIDGET_RESET_ALL');
    this.dialogService.runConfirmDialog({
      title, message, dialogType: DialogType.DELETE, size: 'md'
    }).subscribe(
      (confirmed) => {
        if (confirmed) {
          this.confirmedResetAll();
        }
      }
    );}

  private confirmedResetAll() {
    const newValue: WaaValue[] = [];
    for (const v of this.currentValue) {
      newValue.push({ uuid: v.uuid });
    }
    this.currentValue = newValue;
    this.checkValidity();

    if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
      this.saveWidget();
    }
  }
}

